import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import * as AppPics from 'assets/images';
import useReorderPlayableList from 'hooks/API/GET/useReorderPlayableList';
import useReorderPlayable from 'hooks/API/POST/useReorderPlayable';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import { ReactSortable } from 'react-sortablejs';
import { AppButton } from 'components';

function ReorderPlayable() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { podcastId, episodeId } = useParams();

  const [playables, setPlayables] = useState([]);
  const [previousOrder, setPreviousOrder] = useState([]);

  const {
    data: playableData,
    isLoading,
    refetch,
  } = useReorderPlayableList({
    enabled: true,
    podcastId,
    episodeId,
    queryKey: ['playable-reorder', podcastId, episodeId],
  });

  const { mutate: reorderPlayables, isLoading: isSaving } = useReorderPlayable();

  // Handle initial data loading and refetching
  useEffect(() => {
    if (playableData && Array.isArray(playableData)) {
      const sortedPlayables = [...playableData].sort(
        (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
      );
      setPlayables(sortedPlayables);
      setPreviousOrder(sortedPlayables.map((playable) => playable.id));
    }
  }, [playableData]);

  const handlePlayableReOrder = async (newPlayables) => {
    if (isSaving) return;

    const reorderPayload = newPlayables.map((playable, index) => ({
      id: playable.id,
      sortPosition: index + 1,
    }));

    reorderPlayables(
      { podcastId, episodeId, data: reorderPayload },
      {
        onSuccess: async (res) => {
          notifier.success(res?.data?.message || 'Playables reordered successfully');
          await queryClient.invalidateQueries(['playable-reorder', podcastId, episodeId]);
          await queryClient.invalidateQueries(['playables', podcastId, episodeId]);
          await queryClient.refetchQueries(['playable-reorder', podcastId, episodeId]);
          // Update previous order after successful save
          setPreviousOrder(newPlayables.map((playable) => playable.id));
        },
        onError: (err) => {
          console.error('Reorder error:', err);
          notifier.error(err?.response?.data?.message || 'Failed to reorder playables');
          // Revert to original order on error
          if (playableData && Array.isArray(playableData)) {
            const sortedPlayables = [...playableData].sort(
              (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
            );
            setPlayables(sortedPlayables);
          }
        },
      }
    );
  };

  const handleBackNavigation = () => {
    history.replace(`/podcast/${podcastId}/episode/${episodeId}/playables`);
  };

  // Handle drag end to ensure state is properly updated
  const handleSortEnd = (newState) => {
    setPlayables([...newState]);

    // Check if the order actually changed
    const currentOrder = newState.map((playable) => playable.id);
    const hasOrderChanged = !currentOrder.every((id, index) => id === previousOrder[index]);

    console.log('Checking changes:', {
      hasOrderChanged,
      currentOrder,
      previousOrder,
    });

    // Only call API if order actually changed
    if (hasOrderChanged) {
      handlePlayableReOrder(newState);
    }
  };

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <AppButton variantClass="btn-back" onClick={handleBackNavigation}>
              <img src={AppPics.BackSvg} alt="Back" />
            </AppButton>
          </li>
          <li className="active">Reorder Playables</li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Podcast Home', to: '/podcast' },
                { label: 'Podcast', to: '/podcast/list' },
                { label: 'Episodes', to: `/podcast/${podcastId}/episodes` },
                { label: 'Playables', to: `/podcast/${podcastId}/episode/${episodeId}/playables` },
              ]}
              current="Reorder Playables"
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="tab__contentMenu mb-4">
                <h2 className="prime-head p-0 boldtitle text-nowrap">Reorder Playables</h2>
              </div>
              <div className="tab__contentBody mt-4">
                {isLoading ? (
                  <div className="text-center py-5">
                    <p>Loading playables...</p>
                  </div>
                ) : playables.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted">No playables available to reorder.</p>
                  </div>
                ) : (
                  <ReactSortable
                    list={playables}
                    setList={handleSortEnd}
                    handle=".playableGrabHandleID"
                    animation={200}
                    delayOnTouchStart={true}
                    delay={2}
                    disabled={isSaving}
                  >
                    {playables.map((playable) => (
                      <div className="row mt-3" key={playable.id}>
                        <div className="col-12">
                          <div className="lessonWrap saved lessonWrap--border playableGrabHandleID category-item">
                            {/* Status badge */}
                            {playable?.podcastPlayableFeatured && (
                              <span className={`category-badge featured`}>
                                {playable?.podcastPlayableFeatured ? 'Featured' : ' '}
                              </span>
                            )}
                            <div className="lessonWrap__text">
                              <div className="d-flex align-items-center category-content">
                                <div className="category-info flex-grow-1">
                                  <span className="lessonWrap__name d-block">
                                    {playable.name || playable.playableName}
                                  </span>
                                  {/* <small className="text-muted">ID: {playable.id}</small> */}
                                  {/* <small className="text-muted d-block">
                                    Sort Position: {playable.sortPosition}
                                  </small> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ReactSortable>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReorderPlayable;
